<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.translations.edit.title') }}: <code>{{ translationKey?.key }}</code></h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>

      <form v-else @submit.prevent="submit" class="modal-form">
        <div class="modal-body">
          <div class="form-group">
            <label>{{ $t('bo.translations.create.namespace') }}</label>
            <input v-model="form.namespace" type="text" />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.common.description') }}</label>
            <textarea v-model="form.description" rows="2"></textarea>
          </div>

          <div v-for="locale in locales" :key="locale.code" class="translation-block">
            <div class="translation-header">
              <h3>{{ locale.label }} <span class="locale-code">({{ locale.code.toUpperCase() }})</span></h3>
              <div class="badges">
                <span v-if="!getTranslation(locale.code)" class="badge badge-missing">{{ $t('bo.translations.edit.missing') }}</span>
                <span v-else-if="getTranslation(locale.code)?.is_auto_generated" class="badge badge-auto">{{ $t('bo.translations.edit.autoGenerated') }}</span>
              </div>
            </div>

            <div class="translation-content">
              <textarea
                v-model="form.translations[locale.code]"
                rows="3"
                :placeholder="$t('bo.translations.edit.textPlaceholder', { lang: locale.label })"
                :required="locale.code === 'en'"
              ></textarea>

              <button
                v-if="locale.code === 'fr' && form.translations.en"
                type="button"
                @click="autoTranslate('en', 'fr')"
                :disabled="translating"
                class="btn-auto-translate"
              >
                {{ translating ? $t('bo.translations.edit.translating') : $t('bo.translations.edit.autoTranslateBtn') }}
              </button>
            </div>
          </div>

          <div v-if="error" class="error-message">{{ error }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-secondary">{{ $t('bo.common.cancel') }}</button>
          <button type="submit" :disabled="saving" class="btn-primary">
            {{ saving ? $t('bo.common.saving') : $t('bo.common.save') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import translationService from '@/services/translationService'

const { t } = useI18n()

const props = defineProps({ keyId: { type: Number, required: true } })
const emit = defineEmits(['close', 'updated'])

const translationKey = ref(null)
const locales = ref([])
const loading = ref(true)
const saving = ref(false)
const translating = ref(false)
const error = ref('')

const form = ref({ namespace: '', description: '', translations: { en: '', fr: '' } })

const getTranslation = (localeCode) =>
  translationKey.value?.translations?.find(t => t.locale.code === localeCode)

const loadData = async () => {
  loading.value = true
  try {
    const [keyData, localesData] = await Promise.all([
      translationService.getKey(props.keyId),
      translationService.getLocales()
    ])
    translationKey.value = keyData
    locales.value = localesData
    form.value.namespace = keyData.namespace || ''
    form.value.description = keyData.description || ''
    keyData.translations.forEach(t => {
      form.value.translations[t.locale.code] = t.value
    })
  } catch (err) {
    error.value = t('bo.translations.edit.errorLoad')
  } finally {
    loading.value = false
  }
}

const autoTranslate = async (from, to) => {
  translating.value = true
  error.value = ''
  try {
    const result = await translationService.autoTranslate({
      items: [{ key_id: props.keyId, source_text: form.value.translations[from] }],
      source_locale: from,
      target_locale: to
    })
    if (result.suggestions?.length) {
      const suggestion = result.suggestions.find(s => s.key_id === props.keyId)
      if (suggestion) form.value.translations[to] = suggestion.translated_text
    }
  } catch (err) {
    error.value = 'Auto-translate error: ' + (err.response?.data?.message || err.message)
  } finally {
    translating.value = false
  }
}

const submit = async () => {
  saving.value = true
  error.value = ''
  try {
    await translationService.updateKey(props.keyId, {
      namespace: form.value.namespace,
      description: form.value.description
    })
    const values = Object.entries(form.value.translations)
      .filter(([_, value]) => value)
      .map(([locale_code, value]) => ({ locale_code, value }))
    await translationService.updateTranslations(props.keyId, { values })
    emit('updated')
  } catch (err) {
    error.value = err.response?.data?.message || t('bo.translations.edit.errorSave')
  } finally {
    saving.value = false
  }
}

onMounted(() => loadData())
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.125rem;
  color: #1f2937;
}

.modal-header code {
  background: #f3f4f6;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  color: #6366f1;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
}

.loading {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #374151;
  font-size: 0.875rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.translation-block {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.translation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.translation-header h3 {
  margin: 0;
  font-size: 1rem;
  color: #1f2937;
}

.locale-code {
  color: #6b7280;
  font-weight: 400;
}

.badges { display: flex; gap: 0.5rem; }

.badge {
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
}

.badge-missing { background: #fee2e2; color: #991b1b; }
.badge-auto    { background: #dbeafe; color: #1e40af; }

.translation-content textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  box-sizing: border-box;
}

.translation-content textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-auto-translate {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;
}

.btn-auto-translate:hover:not(:disabled) { background: #e5e7eb; }
.btn-auto-translate:disabled { opacity: 0.5; cursor: not-allowed; }

.error-message {
  padding: 0.75rem;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-primary, .btn-secondary {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
}

.btn-primary { background: #3b82f6; color: white; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-secondary { background: #e5e7eb; color: #374151; }
.btn-secondary:hover { background: #d1d5db; }
</style>